import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useNavigation } from '../context/NavigationContext';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import FormHeader from '../components/FormHeader';
import FormInput from '../components/FormInput';
import PrimaryButton from '../components/PrimaryButton';
import CheckboxRow from '../components/CheckboxRow';

const ForgotPasswordScreen = () => {
  const navigation = useNavigation();
  const { resetPassword } = useAuth();
  const { palette } = useTheme();
  const [email, setEmail] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleResetPassword = async () => {
    setError('');
    
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!agreedToTerms) {
      setError('Please agree to the Terms and Conditions');
      return;
    }

    setLoading(true);

    try {
      const { success, error } = await resetPassword(email.trim());
      if (error || !success) {
        let errorMessage = error || 'Failed to send reset email. Please try again.';
        // Handle specific Firebase errors
        if (errorMessage.includes('user-not-found')) {
          errorMessage = 'No account found with this email address';
        } else if (errorMessage.includes('invalid-email')) {
          errorMessage = 'Invalid email address';
        }
        setError(errorMessage);
      } else {
        setSuccess(true);
        setError('');
        // Auto navigate after 3 seconds
        setTimeout(() => {
          navigation.navigateTo('Login');
        }, 3000);
      }
    } catch (error) {
      setError('Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.mainContent}>
        <View style={styles.formContainer}>
          <FormHeader 
            title="Forgot Password?" 
            subtitle="Enter your email address and we'll send you a link to reset your password." 
          />

          <View style={styles.inputSection}>
            <FormInput
              placeholder="Enter your email"
              value={email}
              onChangeText={setEmail}
              keyboardType="email-address"
              autoCapitalize="none"
              editable={!loading && !success}
              onSubmit={handleResetPassword}
            />

            {error && <Text style={[styles.errorText, { color: '#EA806B' }]}>{error}</Text>}
            {success && (
              <Text style={[styles.successText, { color: palette.primary || '#0DC44A' }]}>
                Reset email sent! Check your inbox and return to sign in.
              </Text>
            )}

            <PrimaryButton 
              title={loading ? 'Sending...' : success ? 'Email Sent!' : 'Send Reset Email'} 
              onPress={handleResetPassword} 
              loading={loading}
              disabled={loading || success}
            />
          </View>

          <View style={styles.checkboxGroup}>
            <CheckboxRow
              checked={agreedToTerms}
              onToggle={() => setAgreedToTerms(!agreedToTerms)}
              disabled={loading || success}
              label={(
                <Text style={[styles.termsText, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
                  I agree to the <Text style={{ color: palette.primary || '#FF0600' }}>Terms and Conditions</Text>
                </Text>
              )}
            />
          </View>

          <TouchableOpacity 
            onPress={() => navigation.navigateTo('Login')}
            disabled={loading}
          >
            <Text style={[styles.backToLogin, { color: palette.text || 'white' }]}>
              Back to Login
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 48,
    paddingTop: 100,
    paddingBottom: 40,
    minHeight: '100vh',
    height: '100%',
  },
  mainContent: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 48,
  },
  formContainer: {
    width: 400,
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 32,
  },
  inputSection: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: 16,
    width: '100%',
  },
  checkboxGroup: {
    alignSelf: 'stretch',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
  },
  termsText: {
    fontSize: 13,
    fontFamily: 'Public Sans',
    fontWeight: '700',
    lineHeight: 16,
  },
  errorText: {
    fontSize: 13,
    fontFamily: 'Public Sans',
    fontWeight: '300',
    textAlign: 'center',
    width: '100%',
    lineHeight: 16,
  },
  successText: {
    fontSize: 13,
    fontFamily: 'Public Sans',
    fontWeight: '300',
    textAlign: 'center',
    width: '100%',
    lineHeight: 16,
  },
  backToLogin: {
    fontSize: 15,
    fontFamily: 'Public Sans',
    fontWeight: '700',
    lineHeight: 20,
    textAlign: 'center',
    textDecorationLine: 'underline',
  },
});

export default ForgotPasswordScreen;

